import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import { Close, ShoppingCart, AttachFile, CheckCircle } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { Course, PurchaseFormData } from '../../types';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { InputAdornment } from '@mui/material';

interface PurchaseModalProps {
  open: boolean;
  onClose: () => void;
  course: Course;
  /** Called after a purchase is submitted successfully so the parent can refetch purchases/access state */
  onPurchaseSubmitted?: () => void;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({ open, onClose, course, onPurchaseSubmitted }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<Omit<PurchaseFormData, 'payment_proof'>>({
    defaultValues: {
      email: user?.email ?? '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        email: user?.email ?? '',
      });
    }
  }, [open, user?.email, reset]);

  const subscriptionType = course.subscription_type;
  const isMonthly = subscriptionType === 'monthly';
  const subscriptionMonths = watch('subscription_months');

  const onSubmit = async (data: Omit<PurchaseFormData, 'payment_proof'>) => {
    try {
      setLoading(true);
      setError('');
      
      if (!selectedFile) {
        setError('Please select a payment proof file');
        return;
      }
      
      const purchaseData: PurchaseFormData = {
        ...data,
        course_id: course.id,
        payment_proof: selectedFile,
        subscription_months: isMonthly ? (data.subscription_months || null) : null,
      };
      
      await apiService.createPurchase(purchaseData);

      onPurchaseSubmitted?.();

      // Show success modal instead of toast
      setShowSuccessModal(true);
      reset();
      setSelectedFile(null);
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Purchase failed';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setError('');
    setSelectedFile(null);
    setShowSuccessModal(false);
    onClose();
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };

  return (
    <>
    {/* Success Modal */}
    <Dialog
      open={showSuccessModal}
      onClose={handleSuccessClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(16, 185, 129, 0.3)',
        }
      }}
    >
      <DialogContent sx={{ textAlign: 'center', py: 5, px: 4 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 8px 30px rgba(16, 185, 129, 0.4)',
          }}
        >
          <CheckCircle sx={{ fontSize: '3rem', color: 'white' }} />
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 2,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Purchase Submitted Successfully!
        </Typography>

        <Alert
          severity="info"
          sx={{
            mt: 3,
            mb: 3,
            textAlign: 'left',
            borderRadius: 2,
            backgroundColor: 'rgba(16, 185, 129, 0.05)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            '& .MuiAlert-icon': {
              color: '#10b981'
            }
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            📧 What's Next?
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Your purchase has been successfully submitted. Access to your purchased course will be provided upon verification, and you will be notified via email accordingly.
          </Typography>
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          Thank you for your patience. Our team will review your payment proof and verify your purchase within 24 hours.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 4, justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSuccessClose}
          sx={{
            px: 6,
            py: 1.5,
            background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
            boxShadow: '0 6px 20px rgba(16, 185, 129, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
              boxShadow: '0 8px 25px rgba(16, 185, 129, 0.4)',
            },
          }}
        >
          Got It!
        </Button>
      </DialogActions>
    </Dialog>

    {/* Purchase Form Modal */}
    <Dialog 
      open={open && !showSuccessModal} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '95vh',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
            borderRadius: '4px',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
            },
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ShoppingCart sx={{ color: '#10b981', fontSize: 24 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Enroll in {course.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" sx={{ color: '#10b981', fontWeight: 600 }}>
                ${course.price}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                ({subscriptionType === 'lifetime' ? 'Lifetime Access' : 'Monthly Subscription'})
              </Typography>
            </Box>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ 
          px: 3, 
          py: 2,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
            borderRadius: '4px',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
            },
          },
        }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            {!user && (
              <TextField
                fullWidth
                required
                label="Email Address"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}

            <FormControl fullWidth sx={{ mt: 2 }} error={!!errors.payment_method} required>
              <InputLabel required>Payment Method</InputLabel>
              <Select
                {...register('payment_method', { required: 'Payment method is required' })}
                error={!!errors.payment_method}
              >
                <MenuItem value="mosan">Mosan</MenuItem>
                <MenuItem value="tpay">Tpay</MenuItem>
                <MenuItem value="transfer_bank">Bank Transfer</MenuItem>
                <MenuItem value="diretamente">Direct Payment</MenuItem>
              </Select>
              {errors.payment_method && (
                <FormHelperText>{errors.payment_method.message}</FormHelperText>
              )}
            </FormControl>

            {isMonthly && (
              <TextField
                fullWidth
                required
                type="number"
                label="Subscription Duration (Months)"
                sx={{ mt: 2 }}
                {...register('subscription_months', {
                  required: 'Subscription duration is required for monthly plans',
                  min: { value: 1, message: 'Minimum 1 month' },
                  max: { value: 24, message: 'Maximum 24 months' },
                  valueAsNumber: true,
                })}
                error={!!errors.subscription_months}
                helperText={errors.subscription_months?.message || 'How many months would you like to subscribe?'}
                inputProps={{ min: 1, max: 24 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">months</InputAdornment>,
                }}
              />
            )}

            {isMonthly && subscriptionMonths && !isNaN(Number(subscriptionMonths)) && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(16, 185, 129, 0.1)', borderRadius: 1, border: '1px solid #10b981' }}>
                <Typography variant="subtitle1" sx={{ color: '#059669', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Amount:</span>
                  <span>${(Number(course.price) * Number(subscriptionMonths)).toFixed(2)}</span>
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Payment Proof (Image) <Typography component="span" color="error.main">*</Typography>
            </Typography>
            <Box
              sx={{
                border: '2px dashed #10b981',
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                backgroundColor: 'rgba(16, 185, 129, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                },
              }}
            >
              <AttachFile sx={{ fontSize: 48, color: '#10b981', mb: 1 }} />
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ 
                  width: '100%', 
                  padding: '8px',
                }}
              />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Upload your payment proof image
              </Typography>
              {selectedFile && (
                <Typography variant="caption" sx={{ color: 'success.main', mt: 1, display: 'block' }}>
                  Selected: {selectedFile.name}
                </Typography>
              )}
              {!selectedFile && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 1, display: 'block' }}>
                  Please select a payment proof file
                </Typography>
              )}
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Important Information:
            </Typography>
            <Typography variant="body2" component="div">
              {subscriptionType === 'lifetime' ? (
                <>
                  This is a <strong>lifetime subscription</strong>. You will have permanent access to this course after verification.
                </>
              ) : (
                <>
                  This is a <strong>monthly subscription</strong>. Please specify how many months you'd like to subscribe.
                  Your access will expire after the selected duration.
                </>
              )}
              <br /><br />
              After submitting your purchase request, please wait for verification.
              You will receive an email confirmation once your payment is verified.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            variant="outlined"
            sx={{
              borderColor: '#10b981',
              color: '#10b981',
              '&:hover': {
                borderColor: '#059669',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{
              backgroundColor: '#10b981',
              '&:hover': {
                backgroundColor: '#059669',
              },
            }}
          >
            {loading ? 'Submitting...' : 'Submit Purchase'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
    </>
  );
};

export default PurchaseModal;
